import * as React from 'react';
import {useSelector} from 'react-redux';

import type {FilterObject} from 'nutshell-core/types';

import {FunnelChart} from 'shells/funnel-report/funnel-chart';
import {Stack} from 'shells/layout/stack/stack';

import type {NutshellState} from '../../../store';

import {useGetChatSettings} from '../../inbox/chat/graphql/hooks';
import {PageContainer} from '../../layout/page-container';
import {RouteContext} from '../../route-context';
import {SettingsSidebar} from '../../setup/settings-sidebar';

import {GAP_SELECT_VALUES_DAY_TO_MONTH, getReportDocumentTitle} from '../report-constants';
import type {ReportKey, ReportURLParams} from '../report-types';
import * as reportSelectors from '../reports-selectors';

import {CompletedActionsChart} from './completed-actions/completed-actions-chart';
import {getCurrentReportChartIsLoading as getCompletedActionsChartIsLoading} from './completed-actions/completed-actions-selectors';
import type {EngagementItemKey} from './consts';
import {FunnelReportEmptyState} from './funnel-report-empty-state';
import {createFunnelChartItems} from './helpers';
import {useGetFunnelFromRedux} from './hooks/use-get-funnel-stats';
import {LeadChart} from './leads/lead-chart';
import {SalesChart} from './sales/sales-chart';
import {VisitorsChart} from './visitors/visitors-chart';
import {WidgetOpensChart} from './widget-opens/widget-opens-chart';

import styles from './engagement-report.module.css';

type Props = {
    reportKey: ReportKey;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => void;
    onParamPush: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'leads'>;
};

export function EngagementReport(props: Props) {
    const {isEmbedded} = React.useContext(RouteContext);

    // Funnel stats are derived from Redux - populated by the chart components below.
    // We don't block on loading because the charts need to render first to fetch their data.
    // The funnel will show 0s initially and update reactively as charts load.
    const {funnelStats} = useGetFunnelFromRedux();
    const {chatSettings, isLoading: isChatSettingsLoading} = useGetChatSettings();

    const chartBucketCount = useSelector((state: NutshellState) =>
        reportSelectors.getCurrentReportChartBucketCount(state)
    );

    // Check if the completed actions chart is still loading
    const isActionsChartLoading = useSelector((state: NutshellState) =>
        getCompletedActionsChartIsLoading(state)
    );

    const chartRefs = React.useRef<
        Partial<Record<EngagementItemKey, HTMLDivElement | null | undefined>>
    >({});

    React.useEffect(() => {
        document.title = getReportDocumentTitle(props.reportKey);
    });

    const funnelChartItems = createFunnelChartItems(
        funnelStats,
        props.activeFilters,
        chartRefs.current,
        isEmbedded
    );

    const currentGapIndex = GAP_SELECT_VALUES_DAY_TO_MONTH.findIndex((gap) => {
        return gap.id === props.urlParams.report.gap;
    });

    // Click-to-zoom handler: when clicking a chart bar, zoom into that time period
    // Note: The chart bucket data uses 'period' as the filter key (filters.period),
    // but the URL uses 'createdTime' (filter.createdTime). We need a custom handler
    // to map between these.
    const handleChartClick = React.useCallback(
        (filters: FilterObject) => {
            // Don't allow zooming in if we're only showing one day
            if (currentGapIndex === 0 && chartBucketCount === 1) {
                return;
            }

            // Extract the period from the clicked bucket (API uses 'period' key)
            const clickedPeriod = filters.period;
            if (!clickedPeriod) {
                return;
            }

            type Params = {
                filter: any;
                report?: any;
                list: {
                    page: number;
                };
            };
            const newParams: Params = {
                // URL uses 'createdTime' as the filter key
                filter: {createdTime: clickedPeriod},
                list: {page: 1},
            };

            // Zoom to a smaller gap if not already at day level
            if (currentGapIndex > 0) {
                newParams.report = {
                    // @ts-expect-error
                    gap: GAP_SELECT_VALUES_DAY_TO_MONTH[currentGapIndex - 1].id,
                };
            }

            props.onParamPush(newParams, true);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentGapIndex, chartBucketCount, props.onParamPush]
    );

    const hasCompletedActions = funnelStats.actions >= 1;
    const isChatEnabled = !isChatSettingsLoading && chatSettings.isChatEnabled;
    const shouldShowEmptyState =
        !isChatSettingsLoading &&
        (!isChatEnabled || (!isActionsChartLoading && !hasCompletedActions));

    return (
        <PageContainer title='Engagement report'>
            <Stack spacing={16}>
                <Stack spacing={16} isHorizontal={true}>
                    <div className={styles['chart-wrapper']}>
                        <FunnelChart
                            items={funnelChartItems}
                            emptyState={{
                                shouldShow: shouldShowEmptyState,
                                component: (
                                    <FunnelReportEmptyState
                                        isChatSettingsLoading={isChatSettingsLoading}
                                        isChatEnabled={isChatEnabled}
                                        hasCompletedActions={hasCompletedActions}
                                    />
                                ),
                            }}
                        />
                    </div>
                    <div className={styles['sidebar-container']}>
                        <SettingsSidebar />
                    </div>
                </Stack>
                <div ref={(el) => (chartRefs.current.visitors = el)}>
                    <VisitorsChart
                        activeFilters={props.activeFilters}
                        onChartClick={handleChartClick}
                        onParamChange={props.onParamChange}
                        reportParams={props.urlParams.report}
                        urlParams={props.urlParams}
                        currentGapIndex={currentGapIndex}
                    />
                </div>
                <div ref={(el) => (chartRefs.current.opens = el)}>
                    <WidgetOpensChart
                        reportParams={props.urlParams.report}
                        activeFilters={props.activeFilters}
                        onChartClick={handleChartClick}
                        onParamChange={props.onParamChange}
                        urlParams={props.urlParams}
                        currentGapIndex={currentGapIndex}
                    />
                </div>
                <div ref={(el) => (chartRefs.current.actions = el)}>
                    <CompletedActionsChart
                        reportParams={props.urlParams.report}
                        activeFilters={props.activeFilters}
                        onChartClick={handleChartClick}
                        onParamChange={props.onParamChange}
                        urlParams={props.urlParams}
                        currentGapIndex={currentGapIndex}
                    />
                </div>
                <div ref={(el) => (chartRefs.current.leads = el)}>
                    <LeadChart
                        reportParams={props.urlParams.report}
                        activeFilters={props.activeFilters}
                        onChartClick={handleChartClick}
                        onParamChange={props.onParamChange}
                        urlParams={props.urlParams}
                        currentGapIndex={currentGapIndex}
                    />
                </div>
                <div ref={(el) => (chartRefs.current.sales = el)}>
                    <SalesChart
                        reportParams={props.urlParams.report}
                        activeFilters={props.activeFilters}
                        onChartClick={handleChartClick}
                        onParamChange={props.onParamChange}
                        urlParams={props.urlParams}
                        currentGapIndex={currentGapIndex}
                    />
                </div>
            </Stack>
        </PageContainer>
    );
}
